import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { createEnquiry } from "../../lib/adventureApi";
import { useMarket } from "../../lib/market";
import BikePicker, { bikeOptionsFor } from "./BikePicker";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEARS = [2026, 2027, 2028];
const PARTY = ["1", "2", "3", "4", "5", "6", "7", "8+"];
const BUDGETS = ["Under $2,000", "$2,000 – $4,000", "$4,000 – $6,000", "$6,000 – $10,000", "$10,000+"];
const HEARD = ["Google search", "Social media", "Word of mouth", "Travel agent", "Blog / press", "Returning traveller", "Other"];
const CODES = ["+44", "+1", "+61", "+91", "+977", "+975", "+92", "+996"];

const field = "h-11 rounded-sm bg-white border-sand-dark text-ink focus-visible:ring-terracotta";
const nativeSel = "h-11 w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 focus:outline-none focus:ring-2 focus:ring-terracotta";
const lbl = "font-mono text-[11px] uppercase tracking-widest text-ink-secondary mb-1.5 block";

function Sel({ id, value, onChange, placeholder, options, className }) {
  return (
    <select data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className={className || nativeSel}>
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o} value={String(o)}>{o}</option>)}
    </select>
  );
}

function Body({ destination, tourSlug, bikeHire, onDone }) {
  const { market } = useMarket();
  const [f, setF] = useState({
    destination: destination || "", month: "", year: "", duration: "", travellers: "", budget: "",
    comments: "", first_name: "", last_name: "", email: "", confirm_email: "",
    phone_code: "+44", phone: "", heard_about: "", newsletter: false,
  });
  const [bikes, setBikes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.destination || !f.travellers || !f.budget || !f.first_name || !f.last_name || !f.email) {
      toast.error("Please fill in the required fields (*)"); return;
    }
    if (f.email !== f.confirm_email) { toast.error("Email addresses don't match"); return; }
    setSubmitting(true);
    try {
      const { confirm_email, ...payload } = f;
      await createEnquiry({ ...payload, market, year: f.year ? Number(f.year) : null, tour_slug: tourSlug || null,
        bike_choices: Object.entries(bikes).filter(([, q]) => q > 0).map(([option, qty]) => ({ option, qty })) });
      setDone(true);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="p-10 text-center" data-testid="enquiry-success">
        <div className="w-14 h-14 rounded-full bg-moss-light flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-moss" /></div>
        <h3 className="font-display font-extrabold text-2xl mt-4">Thank you, {f.first_name}!</h3>
        <p className="text-ink-secondary mt-2 max-w-sm mx-auto">We've received your enquiry about <b>{f.destination}</b>. One of our travel experts will be in touch within one business day.</p>
        <button onClick={onDone} data-testid="enquiry-close-btn" className="mt-6 bg-ink text-white h-11 px-6 rounded-sm font-semibold">Close</button>
      </div>
    );
  }

  return (
    <div className="max-h-[82vh] overflow-y-auto thin-scroll">
      {/* Your Trip */}
      <div className="bg-sand p-6">
        <p className="font-display font-extrabold tracking-tight text-lg mb-4">Your trip</p>
        <div className="space-y-4">
          <div>
            <Label className={lbl}>Where would you like to go? *</Label>
            <Input data-testid="enq-destination" className={field} value={f.destination} onChange={(e) => set("destination")(e.target.value)} placeholder="Country, region or trip name" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className={lbl}>When?</Label>
              <Sel id="enq-month" value={f.month} onChange={set("month")} placeholder="Month" options={MONTHS} />
            </div>
            <div>
              <Label className={lbl}>Year</Label>
              <Sel id="enq-year" value={f.year} onChange={set("year")} placeholder="Year" options={YEARS} />
            </div>
            <div>
              <Label className={lbl}>How long? *</Label>
              <Input data-testid="enq-duration" className={field} value={f.duration} onChange={(e) => set("duration")(e.target.value)} placeholder="e.g. 14 days" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className={lbl}>How many people? *</Label>
              <Sel id="enq-travellers" value={f.travellers} onChange={set("travellers")} placeholder="Select a number" options={PARTY} />
            </div>
            <div>
              <Label className={lbl}>Budget per person *</Label>
              <Sel id="enq-budget" value={f.budget} onChange={set("budget")} placeholder="Select a range" options={BUDGETS} />
            </div>
          </div>
          {bikeHire && <BikePicker options={bikeOptionsFor(bikeHire)} value={bikes} onChange={setBikes} riders={f.travellers} />}
          <div>
            <Label className={lbl}>Any other comments or requests?</Label>
            <Textarea data-testid="enq-comments" className="rounded-sm bg-white border-sand-dark text-ink focus-visible:ring-terracotta min-h-[90px]" value={f.comments} onChange={(e) => set("comments")(e.target.value)} placeholder="E.g. special occasion, any must dos or don'ts" />
          </div>
        </div>
      </div>

      {/* Your Details */}
      <div className="p-6">
        <p className="font-display font-extrabold tracking-tight text-lg mb-4">Your details</p>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className={lbl}>First name *</Label><Input data-testid="enq-first" className={field} value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} /></div>
            <div><Label className={lbl}>Last name *</Label><Input data-testid="enq-last" className={field} value={f.last_name} onChange={(e) => set("last_name")(e.target.value)} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className={lbl}>Email address *</Label><Input data-testid="enq-email" type="email" className={field} value={f.email} onChange={(e) => set("email")(e.target.value)} placeholder="example@email.com" /></div>
            <div><Label className={lbl}>Confirm email *</Label><Input data-testid="enq-email2" type="email" className={field} value={f.confirm_email} onChange={(e) => set("confirm_email")(e.target.value)} placeholder="example@email.com" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className={lbl}>Telephone *</Label>
              <div className="flex gap-2">
                <Sel id="enq-code" value={f.phone_code} onChange={set("phone_code")} placeholder="Code" options={CODES} className={`${nativeSel} w-24`} />
                <Input data-testid="enq-phone" className={`${field} flex-1`} value={f.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="Phone number" />
              </div>
            </div>
            <div>
              <Label className={lbl}>How did you hear about us?</Label>
              <Sel id="enq-heard" value={f.heard_about} onChange={set("heard_about")} placeholder="Select" options={HEARD} />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch data-testid="enq-newsletter" checked={f.newsletter} onCheckedChange={set("newsletter")} />
            <span className="text-sm text-ink-secondary">Sign up to our newsletter for weekly inspiration curated by our travel experts</span>
          </div>
          <button onClick={submit} disabled={submitting} data-testid="enquiry-submit-btn"
            className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit enquiry"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EnquiryForm({ destination, tourSlug, bikeHire, triggerClassName, triggerLabel = "Enquire about this trip", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="enquiry-open-btn" className={triggerClassName}>
          <Mail className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl w-[96vw] p-0 gap-0 overflow-hidden rounded-md" data-testid="enquiry-modal" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Enquire</DialogTitle>
        {open && <Body destination={destination} tourSlug={tourSlug} bikeHire={bikeHire} onDone={() => setOpen(false)} />}
      </DialogContent>
    </Dialog>
  );
}
